import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from './useToast'
import { useTabManager } from './useTabManager'

export function useInactivityTimer() {
  const router = useRouter()
  const { checkForTabClosure, isLastTab, getActiveTabCount } = useTabManager()
  
  // Configurações
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000 // 5 minutos em milissegundos
  const WARNING_TIME = 30 * 1000 // Aviso 30 segundos antes
  const HEARTBEAT_INTERVAL = 5000 // Heartbeat a cada 5 segundos
  const TAB_CHECK_INTERVAL = 5000 // Verificar abas a cada 5 segundos
  
  console.log('🔧 useInactivityTimer: Inicializando composable com timeout de', INACTIVITY_TIMEOUT / 1000, 'segundos')
  
  // Chaves do localStorage
  const LAST_ACTIVITY_KEY = 'lastActivity'
  const HEARTBEAT_KEY = 'heartbeat'
  const SESSION_START_KEY = 'sessionStart'
  const TAB_COUNT_KEY = 'tabCount'
  
  // Estado reativo
  const isWarningVisible = ref(false)
  const remainingTime = ref(0)
  
  // Variáveis internas
  let inactivityTimer = null
  let warningTimer = null
  let countdownTimer = null
  let heartbeatTimer = null
  let tabCheckTimer = null
  let lastActivityTime = Date.now()
  let tabId = generateTabId()
  let isActive = true
  let checkCounter = 0 // Contador para debug
  
  // Lista de eventos que indicam atividade do usuário
  const activityEvents = [
    'mousedown',
    'mousemove', 
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'keydown',
    'touchmove',
    'wheel'
  ]
  
  // Gerar ID único para a aba
  function generateTabId() {
    return 'tab_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }
  
  // Atualizar timestamp de atividade
  const updateActivity = () => {
    
    if (!isActive) {
      return
    }
    
    // VERIFICAÇÃO DE SEGURANÇA: Antes de atualizar atividade, verificar se não houve timeout
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (lastActivity) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity)
      
      // Se passou mais que o timeout, não atualizar atividade - fazer logout
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        performLogout()
        return
      }
    }
    
    lastActivityTime = Date.now()
    localStorage.setItem(LAST_ACTIVITY_KEY, lastActivityTime.toString())
    
    // Resetar timers se houver atividade
    if (isWarningVisible.value) {
      hideWarning()
    }
    resetInactivityTimer()
  }
  
  // Resetar timer de inatividade
  const resetInactivityTimer = () => {
    
    clearTimeout(inactivityTimer)
    clearTimeout(warningTimer)
    
    if (!isUserLoggedIn() || !isActive) {
      return
    }
    
    // Timer para mostrar aviso
    warningTimer = setTimeout(() => {
      if (isActive && isUserLoggedIn()) {
        showWarning()
      }
    }, INACTIVITY_TIMEOUT - WARNING_TIME)
    
    // Timer para logout automático
    inactivityTimer = setTimeout(() => {
      if (isActive && isUserLoggedIn()) {
        performLogout()
      }
    }, INACTIVITY_TIMEOUT)
    
  }
  
  // Mostrar aviso de logout iminente
  const showWarning = () => {
    if (!isActive || !isUserLoggedIn()) return
    
    isWarningVisible.value = true
    remainingTime.value = WARNING_TIME / 1000
    
    toast.warning('Sua sessão expirará em 30 segundos por inatividade!')
    
    // Countdown
    countdownTimer = setInterval(() => {
      remainingTime.value--
      
      if (remainingTime.value <= 0) {
        clearInterval(countdownTimer)
        if (isActive && isUserLoggedIn()) {
          performLogout()
        }
      }
    }, 1000)
  }
  
  // Esconder aviso
  const hideWarning = () => {
    isWarningVisible.value = false
    clearInterval(countdownTimer)
    remainingTime.value = 0
  }
  
  // Continuar sessão (chamado quando usuário interage após aviso)
  const continueSession = () => {
    hideWarning()
    updateActivity()
    toast.success('Sessão renovada!')
  }
  
  // Realizar logout
  const performLogout = () => {
    try {
      isActive = false
      
      // Limpar dados da sessão
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('currentRegion')
      localStorage.removeItem(LAST_ACTIVITY_KEY)
      localStorage.removeItem(HEARTBEAT_KEY)
      localStorage.removeItem(SESSION_START_KEY)
      localStorage.removeItem(TAB_COUNT_KEY)
      
      // Limpar timers
      cleanup()
      
      // Mostrar mensagem e redirecionar
      toast.error('Sessão expirada por inatividade. Faça login novamente.')
      
      // Forçar reload da página para garantir que o App.vue detecte a mudança
      setTimeout(() => {
        window.location.reload()
      }, 1000)
      
    } catch (error) {
      console.error('Erro ao realizar logout:', error)
      window.location.href = '/login'
    }
  }
  
  // Verificar se usuário está logado
  const isUserLoggedIn = () => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true'
    const hasUser = !!localStorage.getItem('user')
    const hasToken = !!localStorage.getItem('token')
    
    // Para o sistema de inatividade, vamos ser menos restritivo
    // Se tem isAuthenticated e user, considerar logado (token pode ter problema de timing)
    const result = isAuthenticated && hasUser
    
    return result
  }
  
  // Sistema de heartbeat para detectar abas ativas
  const sendHeartbeat = () => {
    if (!isActive || !isUserLoggedIn()) return
    
    const heartbeatData = {
      tabId: tabId,
      timestamp: Date.now(),
      lastActivity: lastActivityTime
    }
    
    localStorage.setItem(HEARTBEAT_KEY, JSON.stringify(heartbeatData))
  }
  
  // Verificar inatividade baseado no heartbeat e atividade
  const checkInactivity = () => {
    checkCounter++
    
    if (!isActive || !isUserLoggedIn()) {
      return
    }
    
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (!lastActivity) {
      return
    }
    
    const timeSinceLastActivity = Date.now() - parseInt(lastActivity)
    
    // VERIFICAÇÃO CRÍTICA: Se passou do tempo limite, fazer logout IMEDIATAMENTE
    if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
      performLogout()
      return
    }
    
    // Sincronizar com atividade de outras abas
    if (parseInt(lastActivity) > lastActivityTime) {
      lastActivityTime = parseInt(lastActivity)
      resetInactivityTimer()
    }
  }
  
  // Contar abas ativas
  const updateTabCount = () => {
    if (!isUserLoggedIn()) return
    
    const heartbeatData = localStorage.getItem(HEARTBEAT_KEY)
    if (!heartbeatData) {
      localStorage.setItem(TAB_COUNT_KEY, '1')
      return
    }
    
    try {
      const data = JSON.parse(heartbeatData)
      const timeSinceHeartbeat = Date.now() - data.timestamp
      
      // Se o heartbeat é muito antigo (mais de 10 segundos), provavelmente a aba foi fechada
      if (timeSinceHeartbeat > 10000) {
        // Esta é provavelmente a última aba
        localStorage.setItem(TAB_COUNT_KEY, '1')
      } else {
        // Há outras abas ativas
        localStorage.setItem(TAB_COUNT_KEY, '2')
      }
    } catch (error) {
      localStorage.setItem(TAB_COUNT_KEY, '1')
    }
  }
  
  // Detectar quando a aba está sendo fechada
  const handleBeforeUnload = () => {
    try {
      // O useTabManager já cuida da detecção de fechamento de abas
      // Apenas marcar como fallback
      if (isLastTab.value || getActiveTabCount() <= 1) {
        localStorage.setItem('allTabsClosed', Date.now().toString())
      }
    } catch (error) {
      // Fallback simples
      localStorage.setItem('allTabsClosed', Date.now().toString())
    }
  }
  
  // Detectar quando a aba ganha/perde foco
  const handleVisibilityChange = () => {
    if (document.hidden) {
      // Aba perdeu foco - pausar alguns timers mas manter verificação de inatividade
      isActive = false
    } else {
      // Aba ganhou foco - reativar e verificar inatividade
      isActive = true
      if (isUserLoggedIn()) {
        // VERIFICAÇÃO CRÍTICA: Sempre verificar inatividade quando aba ganha foco
        const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
        if (lastActivity) {
          const timeSinceLastActivity = Date.now() - parseInt(lastActivity)
          
          if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
            performLogout()
            return
          }
        }
        
        updateActivity()
      }
    }
  }
  
  // Verificar se todas as abas foram fechadas na última sessão
  const checkAllTabsClosed = () => {
    if (!isUserLoggedIn()) return false
    
    try {
      // NOVA VERIFICAÇÃO: Verificar tempo total desde última atividade
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
      if (lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity)
        
        // Se passou mais que o timeout de inatividade, fazer logout imediatamente
        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
          performLogout()
          return true
        } else {
          // Se a atividade é recente, limpar sinais de fechamento e continuar
          localStorage.removeItem('allTabsClosed')
          return false
        }
      }
      
      // Usar o novo sistema de detecção de abas
      const closureInfo = checkForTabClosure()
      
      if (closureInfo && closureInfo.wasAllTabsClosed) {
        // Só fazer logout se realmente passou muito tempo
        if (closureInfo.timeSinceClosure >= INACTIVITY_TIMEOUT) {
          performLogout()
          return true
        } else {
          return false
        }
      }
      
      // Verificar sistema antigo como fallback
      const allTabsClosed = localStorage.getItem('allTabsClosed')
      if (allTabsClosed) {
        const closeTime = parseInt(allTabsClosed)
        const timeSinceClosure = Date.now() - closeTime
        
        // Só fazer logout se passou do tempo limite
        if (timeSinceClosure >= INACTIVITY_TIMEOUT) {
          localStorage.removeItem('allTabsClosed')
          performLogout()
          return true
        } else {
          localStorage.removeItem('allTabsClosed')
          return false
        }
      }
      
      return false
    } catch (error) {
      console.warn('Erro ao verificar fechamento de abas:', error)
      return false
    }
  }
  
  // Listener para mudanças no localStorage (comunicação entre abas)
  const handleStorageChange = (event) => {
    if (!isActive || !isUserLoggedIn()) return
    
    // Se a atividade foi atualizada em outra aba
    if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
      const newActivity = parseInt(event.newValue)
      if (newActivity > lastActivityTime) {
        lastActivityTime = newActivity
        resetInactivityTimer()
      }
    }
    
    // Se token foi removido em outra aba, fazer logout local
    if (event.key === 'token' && !event.newValue) {
      performLogout()
    }
    
    // Se todas as abas foram fechadas
    if (event.key === 'allTabsClosed' && event.newValue) {
      setTimeout(() => {
        checkAllTabsClosed()
      }, 100)
    }
  }
  
  // Inicializar sistema de inatividade
  const initInactivityTimer = () => {
    
    const userLoggedIn = isUserLoggedIn()
    
    if (!userLoggedIn) {
      
      // DEBUG: Vamos tentar novamente em 1 segundo caso seja um problema de timing
      setTimeout(() => {
        const userLoggedInRetry = isUserLoggedIn()
        
        if (userLoggedInRetry) {
          initInactivityTimerForced()
        }
      }, 1000)
      
      return
    }
    
    initInactivityTimerForced()
  }
  
  // Função que força a inicialização (sem verificar login)
  const initInactivityTimerForced = () => {
    // PRIMEIRO: Verificar se houve inatividade prolongada
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (lastActivity) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity)
      
      // Se passou mais que o timeout de inatividade, fazer logout imediatamente
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        performLogout()
        return
      }
    } else {
      // Criar atividade inicial
      const now = Date.now()
      lastActivityTime = now
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
    }
    
    // Verificar se houve fechamento de abas
    if (checkAllTabsClosed()) {
      return
    }
    
    // Marcar início da sessão
    if (!localStorage.getItem(SESSION_START_KEY)) {
      localStorage.setItem(SESSION_START_KEY, Date.now().toString())
    }
    
    isActive = true
    
    // Inicializar atividade
    updateActivity()
    
    // Adicionar listeners de atividade
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, true)
    })
    
    // Listener para mudanças no localStorage
    window.addEventListener('storage', handleStorageChange)
    
    // Listener para fechamento de aba/janela
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    // Listener para mudança de visibilidade da aba
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    // Sistema de heartbeat
    heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)
    
    // Verificação periódica de inatividade
    tabCheckTimer = setInterval(() => {
      checkInactivity()
      updateTabCount()
    }, TAB_CHECK_INTERVAL)
    
    // Iniciar timer de inatividade
    resetInactivityTimer()
    
  }
  
  // Limpar listeners e timers
  const cleanup = () => {
    isActive = false
    
    activityEvents.forEach(event => {
      document.removeEventListener(event, updateActivity, true)
    })
    
    window.removeEventListener('storage', handleStorageChange)
    window.removeEventListener('beforeunload', handleBeforeUnload)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    
    clearTimeout(inactivityTimer)
    clearTimeout(warningTimer)
    clearInterval(countdownTimer)
    clearInterval(heartbeatTimer)
    clearInterval(tabCheckTimer)
  }
  
  // Lifecycle hooks para Vue
  onMounted(() => {
    initInactivityTimer()
  })

  onUnmounted(() => {
    cleanup()
  })
  
  // INICIALIZAÇÃO IMEDIATA: Iniciar no setup mesmo
  initInactivityTimer()

  return {
    isWarningVisible,
    remainingTime,
    continueSession,
    performLogout,
    initInactivityTimer,
    cleanup
  }
} 